"use client";

import React from "react";
import { Bot, ShieldCheck, User, TriangleAlert, Layers3 } from "lucide-react";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
export default function RefusalOverGuessing() {
  const headerReveal = useRevealOnScroll({
    threshold: 0.1
  });
  const leftCardReveal = useRevealOnScroll({
    threshold: 0.2
  });
  const rightCardReveal = useRevealOnScroll({
    threshold: 0.2
  });
  return <section id="refusal-over-guessing" className="py-16 bg-background overflow-hidden">
      <div className="container mx-auto px-6 md:px-8 max-w-7xl">
        <div ref={headerReveal.ref} className={cn("max-w-3xl mx-auto text-center mb-12 transition-all duration-700 ease-out", headerReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary mb-6">
            <Layers3 className="size-4" />
            Refusal logic
          </div>

          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground mb-4">
            Refusal is safer than confident guessing
          </h2>

          <p className="text-lg text-muted-foreground">
            In controlled business environments, a wrong answer can do more damage than no answer.
            EloRAG is built to refuse when approved evidence is missing instead of generating something that sounds plausible.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 items-stretch max-w-5xl mx-auto">
          <div ref={leftCardReveal.ref} className={cn("transition-all duration-700 delay-100 ease-out h-full", leftCardReveal.isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8")}>
            <Card className="h-full border-border/50 bg-muted/20 shadow-sm flex flex-col">
              <CardHeader className="border-b border-border/50 pb-4 bg-muted/30 rounded-t-xl">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-background rounded-md border border-border shadow-xs">
                    <Bot className="size-5 text-muted-foreground" />
                  </div>
                  <div>
                    <CardTitle className="text-base font-semibold text-foreground">Generic AI</CardTitle>
                    <p className="text-xs text-muted-foreground mt-0.5">Optimized to answer anyway</p>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="p-6 flex-grow flex flex-col gap-6">
                <div className="flex gap-4 items-start">
                  <div className="size-8 rounded-full bg-secondary flex items-center justify-center shrink-0 mt-1">
                    <User className="size-4 text-secondary-foreground" />
                  </div>
                  <div className="bg-secondary text-secondary-foreground rounded-2xl rounded-tl-sm px-4 py-3 text-sm leading-relaxed">
                    What is the exact budget allocated for Q3 marketing in the new European expansion plan?
                  </div>
                </div>

                <div className="flex gap-4 items-start flex-row-reverse">
                  <div className="size-8 rounded-full bg-background border border-border flex items-center justify-center shrink-0 mt-1">
                    <Bot className="size-4 text-muted-foreground" />
                  </div>
                  <div className="bg-background border border-border text-foreground rounded-2xl rounded-tr-sm px-4 py-3 text-sm leading-relaxed shadow-xs">
                    Based on typical expansion plans, the Q3 marketing budget is likely around $2.5 million, focusing primarily on digital acquisition and local partnerships.
                  </div>
                </div>
              </CardContent>

              <CardFooter className="bg-destructive/5 border-t border-destructive/10 p-4 rounded-b-xl mt-auto">
                <div className="flex items-center gap-2 text-destructive text-sm font-medium">
                  <TriangleAlert className="size-4" />
                  <span>High risk: answer generated without approved evidence</span>
                </div>
              </CardFooter>
            </Card>
          </div>

          <div ref={rightCardReveal.ref} className={cn("transition-all duration-700 delay-200 ease-out h-full", rightCardReveal.isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8")}>
            <Card className="h-full border-primary/20 bg-card shadow-lg ring-1 ring-primary/5 flex flex-col relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none" />

              <CardHeader className="border-b border-border/50 pb-4 bg-card rounded-t-xl relative z-10">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-md border border-primary/20 shadow-xs">
                    <ShieldCheck className="size-5 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-base font-semibold text-foreground">EloRAG Platform</CardTitle>
                    <p className="text-xs text-muted-foreground mt-0.5">Governed RAG delivery</p>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="p-6 flex-grow flex flex-col gap-6 relative z-10">
                <div className="flex gap-4 items-start">
                  <div className="size-8 rounded-full bg-secondary flex items-center justify-center shrink-0 mt-1">
                    <User className="size-4 text-secondary-foreground" />
                  </div>
                  <div className="bg-secondary text-secondary-foreground rounded-2xl rounded-tl-sm px-4 py-3 text-sm leading-relaxed">
                    What is the exact budget allocated for Q3 marketing in the new European expansion plan?
                  </div>
                </div>

                <div className="flex gap-4 items-start flex-row-reverse">
                  <div className="size-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center shrink-0 mt-1 shadow-sm">
                    <ShieldCheck className="size-4" />
                  </div>
                  <div className="bg-primary/5 border border-primary/20 text-foreground rounded-2xl rounded-tr-sm px-4 py-3 text-sm leading-relaxed shadow-xs">
                    I cannot find sufficient evidence in approved sources to answer this question.
                  </div>
                </div>
              </CardContent>

              <CardFooter className="bg-primary/5 border-t border-primary/10 p-4 rounded-b-xl mt-auto relative z-10">
                <div className="flex items-center gap-2 text-primary text-sm font-medium">
                  <ShieldCheck className="size-4" />
                  <span>Safe behavior: no approved evidence, so no answer</span>
                </div>
              </CardFooter>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}